import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Receipt, CreditCard, ShoppingCart, Megaphone, AlertCircle, MessageSquare, X, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { notificationApi, type Notification } from '@/lib/api';
import { cn } from '@/lib/utils';

function getNotificationIcon(notification: Notification) {
  switch (notification.notification_document) {
    case 'invoice':
      return <FileText className="h-5 w-5" />;
    case 'quote':
      return <Receipt className="h-5 w-5" />;
    case 'credit_memo':
      return <CreditCard className="h-5 w-5" />;
    case 'purchase_order':
      return <ShoppingCart className="h-5 w-5" />;
  }

  switch (notification.notification_type) {
    case 'action_required':
      return <AlertCircle className="h-5 w-5" />;
    case 'promotion':
      return <Megaphone className="h-5 w-5" />;
    default:
      return <MessageSquare className="h-5 w-5" />;
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

export function Notifications() {
  const navigate = useNavigate();
  const { currentBusiness } = useApp();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = useCallback(async () => {
    if (!currentBusiness?.id) return;
    const response = await notificationApi.list(currentBusiness.id, 50);
    if (response.data) {
      setNotifications(response.data);
    }
  }, [currentBusiness?.id]);

  const markAllAsRead = useCallback(async () => {
    if (!currentBusiness?.id) return;
    await notificationApi.markAllRead(currentBusiness.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [currentBusiness?.id]);

  const dismissAll = useCallback(async () => {
    if (!currentBusiness?.id) return;
    await notificationApi.dismissAll(currentBusiness.id);
    setNotifications(prev => prev.map(n => ({ ...n, show: false })));
  }, [currentBusiness?.id]);

  const dismissNotification = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await notificationApi.dismiss(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, show: false } : n));
  }, []);

  useEffect(() => {
    fetchNotifications();
    const timeout = setTimeout(() => {
      markAllAsRead();
    }, 500);
    return () => clearTimeout(timeout);
  }, [fetchNotifications, markAllAsRead]);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.cta_link) {
      navigate(notification.cta_link);
    }
  };

  const visibleNotifications = notifications.filter(n => n.show);
  const unreadCount = visibleNotifications.filter(n => !n.read).length;

  return (
    <ListPage>
      <ListPageHeader>
        <ListPageTitle subtitle={unreadCount > 0 ? `${unreadCount} unread` : undefined}>
          Notifications
        </ListPageTitle>
        {visibleNotifications.length > 0 && (
          <ListPageActions>
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <Check className="h-4 w-4 mr-2" />
              Mark all read
            </Button>
            <Button variant="outline" size="sm" onClick={dismissAll}>
              Clear all
            </Button>
          </ListPageActions>
        )}
      </ListPageHeader>

      <ListPageCard>
        <ListPageContent>
          {visibleNotifications.length === 0 ? (
            <div className="py-16 text-center">
              <div className="h-12 w-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-medium mb-1">All caught up</h3>
              <p className="text-sm text-muted-foreground">No notifications to show</p>
            </div>
          ) : (
            <div className="divide-y">
              {visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "flex items-start gap-4 px-6 py-4 cursor-pointer transition-colors hover:bg-muted/50 group",
                    !notification.read && "bg-primary/5"
                  )}
                >
                  {/* Icon */}
                  <div className="shrink-0 mt-0.5">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                      {getNotificationIcon(notification)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm leading-relaxed",
                      !notification.read ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {notification.info_text}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={cn(
                        "text-xs",
                        !notification.read ? "text-primary" : "text-muted-foreground"
                      )}>
                        {formatTimeAgo(notification.created_at)}
                      </span>
                      {notification.cta_title && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          <span className="inline-flex items-center text-xs font-medium text-primary">
                            {notification.cta_title}
                            <ChevronRight className="ml-0.5 h-3 w-3" />
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right side: unread dot + dismiss */}
                  <div className="flex items-center gap-3 shrink-0">
                    {!notification.read && (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                    )}
                    <button
                      onClick={(e) => dismissNotification(e, notification.id)}
                      className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:bg-muted hover:text-muted-foreground transition-all"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ListPageContent>
      </ListPageCard>
    </ListPage>
  );
}
